// =============================================================================
// App Layout Component - Professional Investor Portal Design
// =============================================================================

import { Fragment, useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Transition, Menu, Popover } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  BanknotesIcon,
  MegaphoneIcon,
  EnvelopeIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  BellIcon,
  CalculatorIcon,
  UserGroupIcon,
  FunnelIcon,
  PaperAirplaneIcon,
  UserPlusIcon,
  FolderOpenIcon,
  Cog6ToothIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { useAuth, useAuthStore } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import { SessionTimeoutModal } from '../ui/SessionTimeoutModal';
import { authService } from '../../services/auth.service';
import { investorService } from '../../services/investor.service';
import { adminService } from '../../services/admin.service';
import clsx from 'clsx';

// =============================================================================
// Navigation Items
// =============================================================================

const investorNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Investments', href: '/investments', icon: BuildingOfficeIcon },
  { name: 'Documents', href: '/documents', icon: DocumentTextIcon },
  { name: 'Distributions', href: '/distributions', icon: BanknotesIcon },
  { name: 'Announcements', href: '/announcements', icon: MegaphoneIcon },
  { name: 'Messages', href: '/messages', icon: EnvelopeIcon },
];

// Admin navigation with sections
// Each item has an optional `permArea` mapping to a permission area for TEAM filtering
const adminNavigationSections = [
  {
    items: [
      { name: 'Dashboard', href: '/admin', icon: HomeIcon, permArea: 'dashboard' as const },
    ],
  },
  {
    title: 'ASSET MANAGEMENT',
    items: [
      { name: 'Investors', href: '/admin/investors', icon: UserCircleIcon, permArea: 'investors' as const },
      { name: 'Deals', href: '/admin/deals', icon: BuildingOfficeIcon, permArea: 'deals' as const },
      { name: 'Documents', href: '/admin/documents', icon: DocumentTextIcon, permArea: 'documents' as const },
      { name: 'Distributions', href: '/admin/distributions', icon: BanknotesIcon, permArea: 'distributions' as const },
    ],
  },
  {
    title: 'FUNDRAISING',
    items: [
      { name: 'Contacts', href: '/admin/fundraising/contacts', icon: UserGroupIcon, permArea: 'fundraising' as const },
      { name: 'Pipeline', href: '/admin/fundraising/pipeline', icon: FunnelIcon, permArea: 'fundraising' as const },
      { name: 'Campaigns', href: '/admin/fundraising/campaigns', icon: PaperAirplaneIcon, permArea: 'fundraising' as const },
      { name: 'Conversion', href: '/admin/fundraising/conversion', icon: UserPlusIcon, permArea: 'fundraising' as const },
    ],
  },
  {
    title: 'COMMUNICATION',
    items: [
      { name: 'Announcements', href: '/admin/announcements', icon: MegaphoneIcon, permArea: 'communications' as const },
      { name: 'Messages', href: '/admin/messages', icon: EnvelopeIcon, permArea: 'communications' as const },
    ],
  },
  {
    title: 'E-SIGNATURES',
    items: [
      { name: 'Templates', href: '/admin/esign/templates', icon: PencilSquareIcon, permArea: 'documents' as const },
      { name: 'Envelopes', href: '/admin/esign/envelopes', icon: PencilSquareIcon, permArea: 'documents' as const },
    ],
  },
  {
    title: 'ACCOUNTING',
    items: [
      { name: 'Tax', href: '/admin/temp-access', icon: CalculatorIcon, permArea: 'accounting' as const },
    ],
  },
  {
    title: 'SETTINGS',
    adminOnly: true,
    items: [
      { name: 'Settings', href: '/admin/settings', icon: Cog6ToothIcon },
    ],
  },
];

// Flat list for backwards compatibility
const adminNavigation = adminNavigationSections.flatMap(section => section.items);

const accountantNavigation = [
  { name: 'Deals', href: '/accountant', icon: BuildingOfficeIcon },
];

const prospectNavigation = [
  { name: 'Data Rooms', href: '/prospect/datarooms', icon: FolderOpenIcon },
];

// =============================================================================
// Logo Component
// =============================================================================

function SarsenLogo({ variant = 'light' }: { variant?: 'light' | 'dark' }) {
  const textColor = variant === 'light' ? 'text-white' : 'text-primary-900';

  return (
    <div className="flex items-center gap-3">
      {/* Primary logo - full version with text */}
      <img
        src="/SARSEN%20CAPITAL%20PARTNERS%20F_v2.png"
        alt="Sarsen Capital Partners"
        className="h-10 w-auto"
        onError={(e) => {
          // Fallback to compact logo if full logo not found
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const fallback = target.nextElementSibling as HTMLElement;
          if (fallback) fallback.style.display = 'block';
        }}
      />
      {/* Fallback: compact logo */}
      <img
        src="/Sarsen%20Cap%20Final.png"
        alt="Sarsen Capital Partners"
        className="h-9 w-auto hidden"
        onError={(e) => {
          // Fallback to text logo if neither image found
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const fallback = target.nextElementSibling as HTMLElement;
          if (fallback) fallback.style.display = 'flex';
        }}
      />
      {/* Final fallback: text-based logo */}
      <div className="hidden">
        <div
          className={clsx(
            'w-9 h-9 rounded-lg items-center justify-center font-bold text-lg',
            variant === 'light'
              ? 'bg-white/10 text-white'
              : 'bg-primary-900 text-white'
          )}
          style={{ display: 'flex' }}
        >
          S
        </div>
        <div className={clsx('flex flex-col ml-3', textColor)}>
          <span className="text-sm font-semibold tracking-wide leading-tight">
            SARSEN CAPITAL
          </span>
          <span className={clsx(
            'text-xs tracking-wider',
            variant === 'light' ? 'text-white/70' : 'text-primary-500'
          )}>
            PARTNERS
          </span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Sidebar Navigation Item
// =============================================================================

interface NavItemProps {
  item: {
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
  };
  isActive: boolean;
  onClick?: () => void;
  badge?: number;
}

function NavItem({ item, isActive, onClick, badge }: NavItemProps) {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={clsx(
        'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
        isActive
          ? 'bg-white/10 text-white'
          : 'text-white/70 hover:text-white hover:bg-white/5'
      )}
    >
      <item.icon
        className={clsx(
          'h-5 w-5 flex-shrink-0 transition-colors',
          isActive ? 'text-white' : 'text-white/50 group-hover:text-white/70'
        )}
        aria-hidden="true"
      />
      <span className="flex-1">{item.name}</span>
      {badge !== undefined && badge > 0 && (
        <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

// =============================================================================
// Notification Types
// =============================================================================

interface NotificationItem {
  id: string;
  type: 'document' | 'distribution' | 'announcement' | 'message';
  title: string;
  description: string;
  time: string;
  read: boolean;
  link: string;
}

// =============================================================================
// Notification Bell Component
// =============================================================================

function NotificationBell() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAdmin, isAuthenticated, user } = useAuth();
  const { hasPermission } = usePermissions();
  const isAdminOrTeamBell = isAdmin || user?.role === 'TEAM';
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const items: NotificationItem[] = [];

      if (isAdminOrTeamBell) {
        // Only fetch messages if user has communications.view permission
        if (isAdmin || hasPermission('communications', 'view')) {
          const messages = await adminService.getMessages();
          const unreadMessages = messages.filter(m => !m.readAt && !m.isSentByMe);
          setUnreadCount(unreadMessages.length);

          // Convert unread messages to notification items
          unreadMessages.slice(0, 5).forEach(m => {
            items.push({
              id: m.id,
              type: 'message',
              title: `New message from ${m.fromUser?.name || 'Investor'}`,
              description: m.subject,
              time: new Date(m.createdAt).toLocaleDateString(),
              read: false,
              link: '/admin/messages',
            });
          });
        }
      } else {
        // For investors, get unread announcements and messages
        const [announcements, messages] = await Promise.all([
          investorService.getAnnouncements(),
          investorService.getMessages('all'),
        ]);

        const unreadAnnouncements = announcements.filter(a => !a.readAt);
        const unreadMessages = messages.filter(m => !m.readAt && !m.isSentByMe);
        setUnreadCount(unreadAnnouncements.length + unreadMessages.length);

        // Add unread announcements to notifications
        unreadAnnouncements.slice(0, 3).forEach(a => {
          items.push({
            id: a.id,
            type: 'announcement',
            title: 'New announcement',
            description: a.subject,
            time: new Date(a.sentAt).toLocaleDateString(),
            read: false,
            link: '/announcements',
          });
        });

        // Add unread messages to notifications
        unreadMessages.slice(0, 3).forEach(m => {
          items.push({
            id: m.id,
            type: 'message',
            title: 'New message',
            description: m.subject,
            time: new Date(m.createdAt).toLocaleDateString(),
            read: false,
            link: '/messages',
          });
        });
      }

      setNotifications(items);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setUnreadCount(0);
      setNotifications([]);
    }
  }, [isAdminOrTeamBell, isAuthenticated, isAdmin, hasPermission]);

  useEffect(() => {
    fetchNotifications();
    // Refresh every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Sync with sidebar: listen for badge-decrement events
  useEffect(() => {
    const handleBadgeDecrement = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      let delta = 0;

      if (detail?.messages !== undefined) {
        delta += detail.messages; // negative number
        // Remove specific notification from dropdown if ID provided
        if (detail.messageId) {
          setNotifications((prev) => prev.filter((n) => !(n.type === 'message' && n.id === detail.messageId)));
        }
      }
      if (detail?.announcements !== undefined) {
        delta += detail.announcements; // negative number
        if (detail.announcementId) {
          setNotifications((prev) => prev.filter((n) => !(n.type === 'announcement' && n.id === detail.announcementId)));
        }
      }

      if (delta !== 0) {
        setUnreadCount((prev) => Math.max(0, prev + delta));
      }
    };
    window.addEventListener('badge-decrement', handleBadgeDecrement);
    return () => window.removeEventListener('badge-decrement', handleBadgeDecrement);
  }, []);

  // Sync with sidebar: listen for badge-refresh events (full re-fetch)
  useEffect(() => {
    const handleBadgeRefresh = () => { fetchNotifications(); };
    window.addEventListener('badge-refresh', handleBadgeRefresh);
    return () => window.removeEventListener('badge-refresh', handleBadgeRefresh);
  }, [fetchNotifications]);

  // Handle clicking on a notification - just navigate, don't mark as read
  // User should click on the actual message/announcement to mark it as read
  const handleNotificationClick = (notification: NotificationItem, close: () => void) => {
    close();
    navigate(notification.link);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
      case 'distribution':
        return <BanknotesIcon className="h-5 w-5 text-green-500" />;
      case 'announcement':
        return <MegaphoneIcon className="h-5 w-5 text-purple-500" />;
      case 'message':
        return <EnvelopeIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <Popover className="relative">
      <Popover.Button className="relative p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
        <span className="sr-only">View notifications</span>
        <BellIcon className="h-5 w-5" aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className="absolute right-0 mt-2 w-80 origin-top-right rounded-xl bg-white shadow-elevated ring-1 ring-black/5 focus:outline-none z-50">
          {({ close }) => (
            <>
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                      {unreadCount} new
                    </span>
                  )}
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="py-8 text-center">
                    <BellIcon className="h-10 w-10 text-gray-300 mx-auto" />
                    <p className="mt-2 text-sm text-gray-500">No notifications</p>
                    <p className="text-xs text-gray-400">You're all caught up!</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {notifications.map((notification) => (
                      <button
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification, close)}
                        className={clsx(
                          'w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left',
                          !notification.read && 'bg-primary-50/50'
                        )}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={clsx(
                            'text-sm',
                            notification.read ? 'text-gray-900' : 'font-medium text-gray-900'
                          )}>
                            {notification.title}
                          </p>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {notification.description}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {notification.time}
                          </p>
                        </div>
                        {!notification.read && (
                          <span className="h-2 w-2 rounded-full bg-primary-500 flex-shrink-0 mt-2" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-4 py-3 border-t border-gray-100">
                <button
                  onClick={() => {
                    close();
                    navigate(isAdminOrTeamBell ? '/admin/messages' : '/announcements');
                  }}
                  className="block w-full text-center text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  View all notifications
                </button>
              </div>
            </>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}

// =============================================================================
// User Menu
// =============================================================================

function UserMenu() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
          <span className="text-sm font-semibold text-primary-700">{initials}</span>
        </div>
        <div className="hidden lg:block text-left">
          <p className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
            {user?.name}
          </p>
          <p className="text-xs text-gray-500 truncate max-w-[140px]">
            {isAdmin ? 'Administrator' : user?.role === 'TEAM' ? 'Team Member' : user?.role === 'TEMP_ACCESS' ? 'Accountant' : 'Investor'}
          </p>
        </div>
        <ChevronDownIcon className="hidden lg:block h-4 w-4 text-gray-400" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-elevated ring-1 ring-black/5 focus:outline-none py-1 z-50">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>

          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/profile"
                  className={clsx(
                    'flex items-center gap-3 px-4 py-2 text-sm',
                    active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                  )}
                >
                  <UserCircleIcon className="h-4 w-4" />
                  Your Profile
                </Link>
              )}
            </Menu.Item>
          </div>

          <div className="py-1 border-t border-gray-100">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleLogout}
                  className={clsx(
                    'flex w-full items-center gap-3 px-4 py-2 text-sm',
                    active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                  )}
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  Sign out
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

// =============================================================================
// Component
// =============================================================================

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, isAdmin, isAuthenticated, logout } = useAuth();
  const isTeam = user?.role === 'TEAM';
  const isAdminOrTeam = isAdmin || isTeam;
  const location = useLocation();
  const navigate = useNavigate();

  // Permission checking for sidebar and UI filtering
  const { hasPermission } = usePermissions();

  // Unread counts for sidebar badges
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const fetchUnreadCounts = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      if (isAdminOrTeam) {
        // Only fetch messages if user has communications.view permission
        if (isAdmin || hasPermission('communications', 'view')) {
          const messages = await adminService.getMessages();
          setUnreadMessages(messages.filter(m => !m.readAt && !m.isSentByMe).length);
        }
      } else {
        const [announcements, messages] = await Promise.all([
          investorService.getAnnouncements(),
          investorService.getMessages('all'),
        ]);
        setUnreadAnnouncements(announcements.filter(a => !a.readAt).length);
        setUnreadMessages(messages.filter(m => !m.readAt && !m.isSentByMe).length);
      }
    } catch (err) {
      console.error('Failed to fetch unread counts:', err);
    }
  }, [isAdminOrTeam, isAdmin, isAuthenticated, hasPermission]);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  // Re-fetch when navigating to messages or announcements (user may have read them)
  useEffect(() => {
    fetchUnreadCounts();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for badge-refresh events (dispatched when messages are marked as read)
  useEffect(() => {
    const handleBadgeRefresh = () => { fetchUnreadCounts(); };
    window.addEventListener('badge-refresh', handleBadgeRefresh);
    return () => window.removeEventListener('badge-refresh', handleBadgeRefresh);
  }, [fetchUnreadCounts]);

  // Listen for immediate badge decrement (instant UI update, no API wait)
  useEffect(() => {
    const handleBadgeDecrement = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.messages !== undefined) {
        setUnreadMessages((prev) => Math.max(0, prev + detail.messages));
      }
      if (detail?.announcements !== undefined) {
        setUnreadAnnouncements((prev) => Math.max(0, prev + detail.announcements));
      }
    };
    window.addEventListener('badge-decrement', handleBadgeDecrement);
    return () => window.removeEventListener('badge-decrement', handleBadgeDecrement);
  }, []);

  // Helper to get badge count for a nav item
  const getBadgeCount = (itemName: string): number | undefined => {
    if (itemName === 'Announcements') return unreadAnnouncements;
    if (itemName === 'Messages') return unreadMessages;
    return undefined;
  };

  // Check if user is an accountant (TEMP_ACCESS role) or prospect
  const isAccountant = user?.role === 'TEMP_ACCESS';
  const isProspect = user?.role === 'PROSPECT';

  // Session timeout: 10 minutes total, warning at 5 minutes before
  const handleSessionTimeout = async () => {
    await logout();
    navigate('/login', { state: { message: 'Your session has expired due to inactivity.' } });
  };

  // Refresh token when user clicks "I'm still here"
  const handleExtendSession = useCallback(async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const result = await authService.refreshToken(refreshToken);
    if (!result.accessToken) {
      throw new Error('Failed to refresh token');
    }
    // Update the token in the auth store
    useAuthStore.getState().setAccessToken(result.accessToken);
  }, []);

  const { showWarning, timeRemaining, extendSession } = useSessionTimeout({
    timeout: 10 * 60 * 1000, // 10 minutes total
    warningTime: 5 * 60 * 1000, // 5 minutes warning countdown
    onTimeout: handleSessionTimeout,
    onExtend: handleExtendSession,
  });

  const navigation = isAccountant
    ? accountantNavigation
    : isProspect
      ? prospectNavigation
      : isAdminOrTeam
        ? adminNavigation
        : investorNavigation;

  // Filter admin nav sections for TEAM users based on per-item permissions
  const filteredAdminSections = isTeam
    ? adminNavigationSections
        .filter((section) => {
          // Settings section is admin-only
          if ((section as any).adminOnly) return false;
          return true;
        })
        .map((section) => ({
          ...section,
          // Filter individual items within each section
          items: section.items.filter((item) => {
            const permArea = (item as any).permArea;
            if (!permArea) return true; // no permission mapping = always visible
            return hasPermission(permArea, 'view');
          }),
        }))
        // Remove sections with no visible items
        .filter((section) => section.items.length > 0)
    : adminNavigationSections;

  const isActivePath = (href: string) => {
    if (href === '/dashboard' || href === '/admin' || href === '/accountant' || href === '/prospect/datarooms') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Session Timeout Warning Modal */}
      <SessionTimeoutModal
        isOpen={showWarning}
        timeRemaining={timeRemaining}
        onExtend={extendSession}
        onLogout={handleSessionTimeout}
      />

      {/* Mobile Sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                {/* Mobile Sidebar Content */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-primary-900 px-6 pb-4">
                  <div className="flex h-16 shrink-0 items-center">
                    <SarsenLogo variant="light" />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    {isAdminOrTeam ? (
                      /* Admin/Team: Grouped navigation with sections */
                      <div className="flex flex-1 flex-col gap-y-4">
                        {filteredAdminSections.map((section) => (
                          <div key={section.title || 'dashboard'}>
                            {section.title && (
                              <h3 className="px-3 text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">
                                {section.title}
                              </h3>
                            )}
                            <ul className="flex flex-col gap-y-1">
                              {section.items.map((item) => (
                                <li key={item.name}>
                                  <NavItem
                                    item={item}
                                    isActive={isActivePath(item.href)}
                                    onClick={() => setSidebarOpen(false)}
                                    badge={getBadgeCount(item.name)}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Non-admin: Flat navigation */
                      <ul className="flex flex-1 flex-col gap-y-1">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <NavItem
                              item={item}
                              isActive={isActivePath(item.href)}
                              onClick={() => setSidebarOpen(false)}
                              badge={getBadgeCount(item.name)}
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop Sidebar - starts below the header */}
      <div className="hidden lg:fixed lg:top-16 lg:bottom-0 lg:left-0 lg:z-30 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-primary-900 px-5 py-4">
          <nav className="flex flex-1 flex-col">
            {isAdminOrTeam ? (
              /* Admin/Team: Grouped navigation with sections */
              <div className="flex flex-1 flex-col gap-y-4">
                {filteredAdminSections.map((section, _sectionIndex) => (
                  <div key={section.title || 'dashboard'}>
                    {section.title && (
                      <h3 className="px-3 text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">
                        {section.title}
                      </h3>
                    )}
                    <ul className="flex flex-col gap-y-1">
                      {section.items.map((item) => (
                        <li key={item.name}>
                          <NavItem
                            item={item}
                            isActive={isActivePath(item.href)}
                            badge={getBadgeCount(item.name)}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              /* Non-admin: Flat navigation */
              <ul className="flex flex-1 flex-col gap-y-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <NavItem
                      item={item}
                      isActive={isActivePath(item.href)}
                      badge={getBadgeCount(item.name)}
                    />
                  </li>
                ))}
              </ul>
            )}

            {/* Sidebar Footer */}
            <div className="mt-auto pt-4 border-t border-white/10">
              <div className="flex items-center gap-3 px-3 py-3">
                <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-white/60 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Full-width Header */}
      <header className="fixed top-0 left-0 right-0 z-40 flex h-16 shrink-0 items-center border-b border-gray-200 bg-white">
        {/* Logo section - full width on mobile, sidebar width on desktop */}
        <div className="flex h-full items-center px-4 lg:w-64 lg:px-5">
          {/* Mobile menu button */}
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden mr-3"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Logo - visible always */}
          <SarsenLogo variant="dark" />
        </div>

        {/* Right side header content */}
        <div className="flex flex-1 items-center justify-end gap-x-4 px-4 sm:px-6 lg:px-8">
          {/* Notifications */}
          <NotificationBell />

          {/* Divider */}
          <div className="hidden sm:block h-6 w-px bg-gray-200" aria-hidden="true" />

          {/* User Menu */}
          <UserMenu />
        </div>
      </header>

      {/* Main Content - offset for fixed header */}
      <div className="pt-16 lg:pl-64">
        {/* Page Content */}
        <main className="min-h-[calc(100vh-4rem)] bg-gray-50">
          <div className="py-8">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default AppLayout;
